import type {
    InventoryCategory,
    InventoryPartType,
    InventoryUnit,
} from '../../../types/inventory';

export interface AddItemFormState {
    manufacturerPartNumber: string;
    name: string;
    category: InventoryCategory;
    partType: InventoryPartType;
    supplierId: string;
    unit: InventoryUnit;
    restockAlert: boolean;
    reorderQuantity: number | null;
    targetQuantity: number | null;
    inStockQuantity: number;
    unitCost: number;
    locationId: string;
    partTerminologyId: number | null;
    origin: string;
}

export const emptyAddItemForm = (locationId = ''): AddItemFormState => ({
    manufacturerPartNumber: '',
    name: '',
    category: 'part',
    partType: 'oem',
    supplierId: '',
    unit: 'each',
    restockAlert: false,
    reorderQuantity: null,
    targetQuantity: null,
    inStockQuantity: 0,
    unitCost: 0,
    locationId,
    partTerminologyId: null,
    origin: '',
});

export type AddItemStep = 'identify' | 'lookingUp' | 'details';

/**
 * How the details step was reached: an existing inventory item, a catalog
 * match, a lookup with no match, or the lookup was skipped (or failed).
 */
export type AddItemDetailsVariant = 'existing' | 'match' | 'noMatch' | 'skipped';

/** Form fields the lookup can fill in; editing one drops it from the set. */
export type PrefillField = 'name' | 'partType' | 'unit' | 'unitCost' | 'supplierId';

export type RequiredFieldKey =
    | 'category'
    | 'manufacturerPartNumber'
    | 'name'
    | 'partType'
    | 'supplierId'
    | 'locationId'
    | 'inStockQuantity'
    | 'unit'
    | 'reorderQuantity'
    | 'targetQuantity';

export const REQUIRED_FIELD_LABELS: Record<RequiredFieldKey, string> = {
    category: 'Category',
    manufacturerPartNumber: 'Mfg. Part Number',
    name: 'Name',
    partType: 'Part Type',
    supplierId: 'Supplier',
    locationId: 'Location',
    inStockQuantity: 'In-Stock Quantity',
    unit: 'Unit',
    reorderQuantity: 'Reorder At',
    targetQuantity: 'Restock To',
};

/** Fields that live on the Identify step rather than the details form. */
export const IDENTIFY_FIELDS: ReadonlySet<RequiredFieldKey> = new Set<RequiredFieldKey>([
    'category',
    'manufacturerPartNumber',
]);

export type AddItemSectionId = 'details' | 'location' | 'cost' | 'photos';

export const ADD_ITEM_FIELD_ATTR = 'data-add-item-field';

export const addItemFieldProps = (key: RequiredFieldKey) => ({
    [ADD_ITEM_FIELD_ATTR]: key,
});

const FOCUSABLE =
    'input:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Brings a details-form field into view and focuses its first control.
 * `reveal` expands a collapsed section and scrolls; without it, falls back to
 * `scrollIntoView`.
 */
export const focusAddItemField = async (
    key: RequiredFieldKey,
    reveal?: (element: Element) => Promise<void>,
) => {
    const wrapper = document.querySelector<HTMLElement>(`[${ADD_ITEM_FIELD_ATTR}="${key}"]`);
    if (!wrapper) return;
    if (reveal) {
        await reveal(wrapper);
    } else {
        wrapper.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    wrapper.querySelector<HTMLElement>(FOCUSABLE)?.focus({ preventScroll: true });
};
